import React, {useEffect, useState} from "react";
import {
    Button,
    CircularProgress,
    ClickAwayListener,
    Fade,
    Grid,
    Popper,
    Typography
} from "@mui/material";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import EnhancedAutocomplete from "../../../components/EnhancedAutocomplete";
import {utilGetSupplierGroups, utilUpdateSupplierGroup, utilDeleteSupplierGroup} from "../../../utils/utils";
import {BulkUpdateStatus} from "../../../components/CatalogManagerResults";

interface UngroupSupplierPopoverProps {
    anchorEl?: any;
    open: boolean;
    setOpen: (open: boolean) => void;
    partnerIds: (number | string)[];
    onGroup?: () => void;
}

// Ungroup Popover Component (for removing suppliers from groups)
export const UngroupSupplierPopover = (props: UngroupSupplierPopoverProps) => {
    const {anchorEl, open, setOpen, partnerIds, onGroup} = props;
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState({open: false, errorHeader: "", errorMessage: ""});
    const [availableGroups, setAvailableGroups] = useState<string[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

    useEffect(() => {
        const loadGroups = async () => {
            try {
                const groups = await utilGetSupplierGroups();
                // Success resolves to an array; failure to {errorMessage} (no `open`, and errorMessage may be
                // undefined). Gate on Array.isArray so the error object never reaches the .filter/.map below.
                if (Array.isArray(groups)) {
                    const relevantGroups = groups.filter((group: any) => {
                        const partnersInGroup = group.Partners ? group.Partners.map((p: any) => p.Id) : [];
                        return partnerIds.some(id => partnersInGroup.includes(id));
                    });
                    const sortedGroupNames = relevantGroups.map((g: any) => g.Name).sort((a: string, b: string) =>
                        a.toLowerCase().localeCompare(b.toLowerCase(), undefined, { numeric: true, sensitivity: 'base' })
                    );
                    setAvailableGroups(sortedGroupNames);
                }
            } catch (err: any) {
                setError({open: true, errorHeader: "Error", errorMessage: err.message});
            }
        };

        if (open) {
            loadGroups();
            setSelectedGroups([]);
        }
    }, [open, partnerIds]);

    const handleSubmit = async () => {
        setSubmitting(true);
        const errors: string[] = [];

        try {
            // Remove suppliers from selected groups
            for (const groupName of selectedGroups) {
                const groups = await utilGetSupplierGroups();
                const group = groups.find((g: any) => g.Name === groupName);
                if (group) {
                    const currentPartnerIds = group.Partners ? group.Partners.map((p: any) => p.Id) : [];
                    const updatedPartnerIds = currentPartnerIds.filter((id: any) => !partnerIds.includes(id));
                    
                    if (updatedPartnerIds.length !== currentPartnerIds.length) {
                        // If no suppliers left in the group, delete the group entirely
                        if (updatedPartnerIds.length === 0) {
                            const deleteResult = await utilDeleteSupplierGroup(group.Id || groupName);
                            if (deleteResult.errorMessage) {
                                errors.push(deleteResult.errorMessage);
                            }
                        } else {
                            // Otherwise, update the group with remaining suppliers
                            const updateResult = await utilUpdateSupplierGroup(groupName, null, updatedPartnerIds);
                            if (updateResult.errorMessage) {
                                errors.push(updateResult.errorMessage);
                            }
                        }
                    }
                }
            }

            if (errors.length > 0) {
                setError({open: true, errorHeader: "Error", errorMessage: errors.join(", ")});
            } else {
                setOpen(false);
                setSelectedGroups([]);
                if (onGroup) onGroup();
            }
        } catch (err: any) {
            setError({open: true, errorHeader: "Error", errorMessage: err.message});
        }

        setSubmitting(false);
    };

    return (
        <>
            <Popper open={open} anchorEl={anchorEl} transition placement="bottom-start" style={{zIndex: 1300}}>
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={() => {
                        if (!submitting) {
                            setOpen(false);
                            setSelectedGroups([]);
                        }
                    }}>
                        <Fade {...TransitionProps} timeout={350}>
                            <BulkUpdateStatus container>
                                <Typography>Remove suppliers from groups</Typography>

                                <EnhancedAutocomplete
                                    value={selectedGroups}
                                    options={availableGroups.filter(group => !selectedGroups.includes(group))}
                                    onChange={(e, value) => setSelectedGroups(value)}
                                    placeholder={selectedGroups.length > 0 ? "" : "Select groups to remove from"}
                                    multiple
                                />

                                <Grid item container style={{width: "100%", gap: "8px"}} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        style={{padding: "6px 12px"}}
                                        onClick={() => {
                                            setOpen(false);
                                            setSelectedGroups([]);
                                        }}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        style={{padding: "6px 12px"}}
                                        disabled={selectedGroups.length === 0 || submitting}
                                        onClick={handleSubmit}
                                    >
                                        {submitting ? <CircularProgress size={16} style={{color: 'white'}}/> : "Save"}
                                    </Button>
                                </Grid>
                            </BulkUpdateStatus>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            <EnhancedErrorDialog
                open={error.open}
                errorHeader={error.errorHeader}
                errorMessage={error.errorMessage}
                onClose={() => setError({open: false, errorHeader: "", errorMessage: ""})}
            />
        </>
    );
};

export default UngroupSupplierPopover;
